import { useState, useEffect } from 'react';
import { FileText, RotateCcw, Save, Hash, Settings, FileEdit, Eye } from 'lucide-react';

const InvoiceSettings = ({ settings, onSave, onChange }) => {
  const [formData, setFormData] = useState({
    invoicePrefix: settings?.invoicePrefix || 'INV',
    quotePrefix: settings?.quotePrefix || 'QTE',
    invoiceStartNumber: settings?.invoiceStartNumber || 1,
    quoteStartNumber: settings?.quoteStartNumber || 1,
    useYearInNumbers: settings?.useYearInNumbers || false,
    // Document settings
    showItemCodes: settings?.showItemCodes || false,
    showItemDescription: settings?.showItemDescription || true,
    showDiscountColumn: settings?.showDiscountColumn || false,
    defaultNotes: settings?.defaultNotes || '',
    defaultTerms: settings?.defaultTerms || '',
    // Auto-features
    autoGenerateNumbers: settings?.autoGenerateNumbers !== false,
    autoSaveDrafts: settings?.autoSaveDrafts !== false,
    requirePoNumber: settings?.requirePoNumber || false
  });

  const [activeTab, setActiveTab] = useState('numbering');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (onChange) {
      onChange();
    }
  }, [formData, onChange]);

  const handleSubmit = () => {
    onSave({ ...settings, ...formData });
  };

  const generatePreviewNumber = (prefix, startNumber, useYear) => {
    const year = useYear ? new Date().getFullYear() + '-' : '';
    return `${prefix}-${year}${String(startNumber).padStart(3, '0')}`;
  };

  const tabs = [
    { id: 'numbering', label: 'Numbering', icon: Hash },
    { id: 'display', label: 'Display Options', icon: Settings },
    { id: 'content', label: 'Default Content', icon: FileEdit }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'numbering':
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Document Numbering</h3>
              <p className="text-blue-700 text-sm mb-6">Configure how your invoices and quotes are numbered</p>
              
              {/* Year Toggle */}
              <div className="bg-white border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <input
                    className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mt-0.5"
                    type="checkbox"
                    id="useYearInNumbers"
                    checked={formData.useYearInNumbers}
                    onChange={(e) => setFormData({...formData, useYearInNumbers: e.target.checked})}
                  />
                  <div className="ml-3">
                    <label className="text-sm font-medium text-gray-900" htmlFor="useYearInNumbers">
                      Include year in document numbers
                    </label>
                    <p className="text-sm text-gray-600 mt-1">
                      Numbers will include the current year (e.g., INV-2025-001 instead of INV-001)
                    </p>
                  </div>
                </div>
              </div>

              {/* Invoice Numbering */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <FileText size={16} className="text-blue-600" />
                    </div>
                    Invoice Settings
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prefix</label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono text-center"
                        value={formData.invoicePrefix}
                        onChange={(e) => setFormData({...formData, invoicePrefix: e.target.value.toUpperCase()})}
                        placeholder="INV"
                        maxLength="5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Starting Number</label>
                      <input
                        type="number"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-center"
                        value={formData.invoiceStartNumber}
                        onChange={(e) => setFormData({...formData, invoiceStartNumber: parseInt(e.target.value) || 1})}
                        min="1"
                        max="99999"
                      />
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                      <p className="text-sm font-medium text-blue-800 mb-1">Preview:</p>
                      <code className="text-blue-600 font-mono text-lg">
                        {generatePreviewNumber(formData.invoicePrefix, formData.invoiceStartNumber, formData.useYearInNumbers)}
                      </code>
                    </div>
                  </div>
                </div>

                {/* Quote Numbering */}
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center mr-3">
                      <FileText size={16} className="text-cyan-600" />
                    </div>
                    Quote Settings
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prefix</label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono text-center"
                        value={formData.quotePrefix}
                        onChange={(e) => setFormData({...formData, quotePrefix: e.target.value.toUpperCase()})}
                        placeholder="QTE"
                        maxLength="5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Starting Number</label>
                      <input
                        type="number"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-center"
                        value={formData.quoteStartNumber}
                        onChange={(e) => setFormData({...formData, quoteStartNumber: parseInt(e.target.value) || 1})}
                        min="1"
                        max="99999"
                      />
                    </div>
                    <div className="bg-cyan-50 border border-cyan-200 rounded-md p-3">
                      <p className="text-sm font-medium text-cyan-800 mb-1">Preview:</p>
                      <code className="text-cyan-600 font-mono text-lg">
                        {generatePreviewNumber(formData.quotePrefix, formData.quoteStartNumber, formData.useYearInNumbers)}
                      </code>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <span className="font-medium">⚠️ Important:</span> Changes to prefixes and numbering will only apply to new documents. Existing invoices and quotes will keep their current numbers.
                </p>
              </div>
            </div>
          </div>
        );

      case 'display':
        return (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-green-900 mb-2">Display Options</h3>
              <p className="text-green-700 text-sm mb-6">Customize what appears on your invoices and quotes</p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Column Options */}
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 mb-4">Table Columns</h4>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <input
                        className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2 mt-0.5"
                        type="checkbox"
                        id="showItemCodes"
                        checked={formData.showItemCodes}
                        onChange={(e) => setFormData({...formData, showItemCodes: e.target.checked})}
                      />
                      <div className="ml-3">
                        <label className="text-sm font-medium text-gray-900" htmlFor="showItemCodes">
                          Show item codes
                        </label>
                        <p className="text-sm text-gray-600">Display SKU/product codes in line items</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <input
                        className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2 mt-0.5"
                        type="checkbox"
                        id="showDiscountColumn"
                        checked={formData.showDiscountColumn}
                        onChange={(e) => setFormData({...formData, showDiscountColumn: e.target.checked})}
                      />
                      <div className="ml-3">
                        <label className="text-sm font-medium text-gray-900" htmlFor="showDiscountColumn">
                          Show discount column
                        </label>
                        <p className="text-sm text-gray-600">Add a discount column to line items</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Business Rules */}
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 mb-4">Business Rules</h4>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <input
                        className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2 mt-0.5"
                        type="checkbox"
                        id="requirePoNumber"
                        checked={formData.requirePoNumber}
                        onChange={(e) => setFormData({...formData, requirePoNumber: e.target.checked})}
                      />
                      <div className="ml-3">
                        <label className="text-sm font-medium text-gray-900" htmlFor="requirePoNumber">
                          Require PO number
                        </label>
                        <p className="text-sm text-gray-600">Make purchase order number mandatory</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Automation Settings */}
              <div className="bg-white border border-gray-200 rounded-lg p-5 mt-6">
                <h4 className="font-semibold text-gray-800 mb-4">Automation</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="flex items-start">
                    <input
                      className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2 mt-0.5"
                      type="checkbox"
                      id="autoGenerateNumbers"
                      checked={formData.autoGenerateNumbers}
                      onChange={(e) => setFormData({...formData, autoGenerateNumbers: e.target.checked})}
                    />
                    <div className="ml-3">
                      <label className="text-sm font-medium text-gray-900" htmlFor="autoGenerateNumbers">
                        Auto-generate document numbers
                      </label>
                      <p className="text-sm text-gray-600">Automatically assign next number when creating documents</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <input
                      className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2 mt-0.5"
                      type="checkbox"
                      id="autoSaveDrafts"
                      checked={formData.autoSaveDrafts}
                      onChange={(e) => setFormData({...formData, autoSaveDrafts: e.target.checked})}
                    />
                    <div className="ml-3">
                      <label className="text-sm font-medium text-gray-900" htmlFor="autoSaveDrafts">
                        Auto-save drafts
                      </label>
                      <p className="text-sm text-gray-600">Automatically save work every 30 seconds</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'content':
        return (
          <div className="space-y-6">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-purple-900 mb-2">Default Content</h3>
              <p className="text-purple-700 text-sm mb-6">Set standard text that appears on new documents</p>
              
              <div className="space-y-6">
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Default Notes</label>
                  <textarea
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                    rows="4"
                    value={formData.defaultNotes}
                    onChange={(e) => setFormData({...formData, defaultNotes: e.target.value})}
                    placeholder="Thank you for your business. Payment is due within 30 days of invoice date. Please reference the invoice number when making payment..."
                  />
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-sm text-gray-500">
                      These notes will be pre-filled on new invoices and quotes
                    </p>
                    <p className="text-xs text-gray-400">{formData.defaultNotes.length}/500 characters</p>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Default Terms & Conditions</label>
                  <textarea
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                    rows="5"
                    value={formData.defaultTerms}
                    onChange={(e) => setFormData({...formData, defaultTerms: e.target.value})}
                    placeholder="1. Payment is due within 30 days of invoice date&#10;2. Late payments may incur additional charges&#10;3. Goods remain property of seller until payment is received&#10;4. Returns accepted within 14 days with original receipt..."
                  />
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-sm text-gray-500">
                      Standard terms that apply to all transactions
                    </p>
                    <p className="text-xs text-gray-400">{formData.defaultTerms.length}/1000 characters</p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <span className="font-medium">💡 Tip:</span> You can always edit these on individual invoices and quotes. This just sets the starting template.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getActiveFeatures = () => {
    const features = [];
    if (formData.autoGenerateNumbers) features.push({ label: 'Auto-numbering', color: 'green' });
    if (formData.autoSaveDrafts) features.push({ label: 'Auto-save', color: 'cyan' });
    if (formData.requirePoNumber) features.push({ label: 'PO Required', color: 'yellow' });
    if (formData.showItemCodes) features.push({ label: 'Item Codes', color: 'gray' });
    if (formData.showDiscountColumn) features.push({ label: 'Discounts', color: 'gray' });
    if (formData.useYearInNumbers) features.push({ label: 'Year in Numbers', color: 'blue' });
    return features;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-start">
          <div>
            <h5 className="flex items-center text-xl font-semibold text-gray-800">
              <FileText size={24} className="mr-3" />
              Invoice & Quote Settings
            </h5>
            <p className="text-sm text-gray-600 mt-1">
              Configure numbering, formatting, and default content for your documents
            </p>
          </div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Eye size={16} className="mr-2" />
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-1 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon size={16} className="mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {renderTabContent()}

        {/* Preview Section */}
        {showPreview && (
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h6 className="text-lg font-semibold text-gray-800 mb-4">Settings Overview</h6>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Next Document Numbers</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <span className="font-medium text-blue-800">Next Invoice:</span>
                      <code className="text-blue-600 font-mono text-lg">
                        {generatePreviewNumber(formData.invoicePrefix, formData.invoiceStartNumber, formData.useYearInNumbers)}
                      </code>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
                      <span className="font-medium text-cyan-800">Next Quote:</span>
                      <code className="text-cyan-600 font-mono text-lg">
                        {generatePreviewNumber(formData.quotePrefix, formData.quoteStartNumber, formData.useYearInNumbers)}
                      </code>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Active Features</h4>
                  <div className="flex flex-wrap gap-2">
                    {getActiveFeatures().map((feature, index) => (
                      <span
                        key={index}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          feature.color === 'green' ? 'bg-green-100 text-green-800' :
                          feature.color === 'cyan' ? 'bg-cyan-100 text-cyan-800' :
                          feature.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                          feature.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {feature.label}
                      </span>
                    ))}
                    {getActiveFeatures().length === 0 && (
                      <span className="text-sm text-gray-500 italic">No special features enabled</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between pt-8 border-t border-gray-200 mt-8">
          <button 
            type="button" 
            className="flex items-center px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RotateCcw size={16} className="mr-2" />
            Reset to Defaults
          </button>
          <button 
            type="button"
            onClick={handleSubmit}
            className="flex items-center px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save size={16} className="mr-2" />
            Save All Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceSettings;